import React, { useEffect, useState } from "react";
import {
  Alert,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  AGENT_MASTER_EDITABLE_CONTAINER_STATUSES,
  mapContainerStatusToBookingStatus,
} from "../../config/agentPortal";
import {
  bulkUpdateBookingsInMaster,
  bulkUpdateContainersInMaster,
  getUpdatePreview,
  updateMasterContainer,
} from "../../services/masterContainerService";
import type { MasterContainerWithContainers } from "../../types/booking";
import {
  formatBookingEffReferenceFromParts,
  formatDate,
} from "../../utils/bookingUtils";
import {
  BulkUpdateDiffPreview,
  bookingStatusTag,
} from "../containers/BulkUpdateDiffPreview";

const { Title } = Typography;
const { Option } = Select;

interface AgentMasterBulkUpdateModalProps {
  master: MasterContainerWithContainers | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AgentMasterBulkUpdateModal: React.FC<
  AgentMasterBulkUpdateModalProps
> = ({ master, open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [updatePreview, setUpdatePreview] = useState<{
    containers: Array<{
      id: number;
      reference: string | null;
      status: string | null;
      eta: string | null;
      etd: string | null;
      track_and_trace: string | null;
    }>;
    bookings: Array<{
      id: number;
      reference: string | null;
      status: string | null;
      container_id: number | null;
    }>;
  }>({ containers: [], bookings: [] });

  const watchedContainerStatus = Form.useWatch("container_status", form);
  const watchedEta = Form.useWatch("eta", form);
  const watchedEtd = Form.useWatch("etd", form);
  const watchedDelayedEtd = Form.useWatch("delayed_etd", form);
  const watchedTrackAndTrace = Form.useWatch("track_and_trace", form);

  useEffect(() => {
    if (!open || !master) return;

    form.setFieldsValue({
      eta: master.eta ? dayjs(master.eta) : undefined,
      etd: master.etd ? dayjs(master.etd) : undefined,
      delayed_etd: master.delayed_etd ? dayjs(master.delayed_etd) : undefined,
      container_status: master.status || undefined,
      track_and_trace: master.track_and_trace || undefined,
    });

    setPreviewLoading(true);
    getUpdatePreview(master.id)
      .then((result) => {
        if (result.error) {
          message.error("Failed to load update preview");
          return;
        }
        setUpdatePreview({
          containers: result.containers,
          bookings: result.bookings,
        });
      })
      .finally(() => setPreviewLoading(false));
  }, [open, master, form]);

  const handleClose = () => {
    form.resetFields();
    setUpdatePreview({ containers: [], bookings: [] });
    onClose();
  };

  const handleSubmit = async () => {
    if (!master) return;

    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const containerUpdates: {
        eta?: string | null;
        etd?: string | null;
        delayed_etd?: string | null;
        status?: string | null;
        track_and_trace?: string | null;
      } = {};

      if (values.eta !== undefined) {
        containerUpdates.eta = values.eta
          ? dayjs(values.eta).format("YYYY-MM-DD")
          : null;
      }
      if (values.etd !== undefined) {
        containerUpdates.etd = values.etd
          ? dayjs(values.etd).format("YYYY-MM-DD")
          : null;
      }
      if (values.delayed_etd !== undefined) {
        containerUpdates.delayed_etd = values.delayed_etd
          ? dayjs(values.delayed_etd).format("YYYY-MM-DD")
          : null;
      }
      if (values.container_status !== undefined) {
        containerUpdates.status = values.container_status || null;
      }
      if (Object.prototype.hasOwnProperty.call(values, "track_and_trace")) {
        containerUpdates.track_and_trace =
          values.track_and_trace === "" ? null : values.track_and_trace || null;
      }

      const masterResult = await updateMasterContainer(master.id, {
        reference: master.reference || undefined,
        eta: containerUpdates.eta,
        etd: containerUpdates.etd,
        delayed_etd: containerUpdates.delayed_etd,
        status: containerUpdates.status,
        track_and_trace: containerUpdates.track_and_trace,
      });

      if (masterResult.error) {
        throw new Error("Failed to update Master BL/AWB");
      }

      if (Object.keys(containerUpdates).length > 0) {
        const containerResult = await bulkUpdateContainersInMaster(
          master.id,
          containerUpdates
        );
        if (!containerResult.success) {
          throw new Error("Failed to update containers");
        }
      }

      // Agents may only push bookings to in_transit via planned/departed/arrived
      if (
        values.container_status &&
        ["planned", "departed", "arrived"].includes(values.container_status)
      ) {
        const bookingResult = await bulkUpdateBookingsInMaster(master.id, {
          status: mapContainerStatusToBookingStatus(values.container_status),
        });
        if (!bookingResult.success) {
          throw new Error("Failed to update bookings");
        }
      }

      message.success("Update completed successfully");
      handleClose();
      onSuccess();
    } catch (error) {
      console.error("Error performing agent bulk update:", error);
      message.error("Failed to perform update");
    } finally {
      setSubmitting(false);
    }
  };

  const bookingNewStatus =
    watchedContainerStatus &&
    ["planned", "departed", "arrived"].includes(watchedContainerStatus)
      ? mapContainerStatusToBookingStatus(watchedContainerStatus)
      : null;

  return (
    <Modal
      title={`Update: ${master?.reference || `Master #${master?.id}`}`}
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={submitting}
      width={1000}
      style={{ top: 20 }}
      destroyOnClose
    >
      <Alert
        message="Update"
        description="This updates all containers and bookings under this Master BL/AWB. Booking status is set to In Transit when container status is Planned, Departed, or Arrived."
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Title level={5}>Container updates</Title>
            <Form.Item name="eta" label="ETA">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="etd"
              label="ETD (original)"
              tooltip="Original booked departure — used for invoicing"
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="delayed_etd"
              label="Delayed ETD"
              tooltip="Optional operational delayed departure"
            >
              <DatePicker style={{ width: "100%" }} allowClear />
            </Form.Item>
            <Form.Item name="container_status" label="Container status">
              <Select placeholder="Select container status" allowClear>
                {AGENT_MASTER_EDITABLE_CONTAINER_STATUSES.map((status) => (
                  <Option key={status.value} value={status.value}>
                    {status.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="track_and_trace" label="Track & Trace">
              <Input placeholder="Enter track and trace information" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Title level={5}>Booking updates</Title>
            <Alert
              message="Automatic status mapping"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    <strong>planned / departed / arrived</strong> → in_transit
                  </li>
                </ul>
              }
              type="info"
              showIcon
            />
          </Col>
        </Row>

        <Divider />

        <Title level={5} style={{ marginBottom: 12 }}>
          Preview of Changes
        </Title>
        {previewLoading ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin size="large" />
            <div style={{ marginTop: 8 }}>Loading preview...</div>
          </div>
        ) : (
          <>
            <BulkUpdateDiffPreview
              title={`Containers (${updatePreview.containers.length})`}
              emptyText="No containers to update"
              entities={updatePreview.containers.map((container) => {
                const newStatus = watchedContainerStatus || null;
                const newEta = watchedEta
                  ? dayjs(watchedEta).format("DD/MM/YYYY")
                  : null;
                const newEtd = watchedEtd
                  ? dayjs(watchedEtd).format("DD/MM/YYYY")
                  : null;
                const newDelayedEtd = watchedDelayedEtd
                  ? dayjs(watchedDelayedEtd).format("DD/MM/YYYY")
                  : null;
                const newTrack = watchedTrackAndTrace || null;

                return {
                  key: container.id,
                  title: container.reference || `Container ${container.id}`,
                  fields: [
                    {
                      label: "Status",
                      oldValue: container.status || "—",
                      newValue: newStatus || container.status || "—",
                      changed:
                        !!newStatus && newStatus !== (container.status || null),
                    },
                    {
                      label: "ETA",
                      oldValue: formatDate(container.eta) || "—",
                      newValue: newEta || formatDate(container.eta) || "—",
                      changed: !!watchedEta,
                    },
                    {
                      label: "ETD",
                      oldValue: formatDate(container.etd) || "—",
                      newValue: newEtd || formatDate(container.etd) || "—",
                      changed: !!watchedEtd,
                    },
                    {
                      label: "Delayed ETD",
                      oldValue: formatDate(container.delayed_etd) || "—",
                      newValue:
                        watchedDelayedEtd === undefined
                          ? formatDate(container.delayed_etd) || "—"
                          : newDelayedEtd || "—",
                      changed: watchedDelayedEtd !== undefined,
                    },
                    {
                      label: "Track & Trace",
                      oldValue: container.track_and_trace || "—",
                      newValue: newTrack || container.track_and_trace || "—",
                      changed:
                        watchedTrackAndTrace !== undefined &&
                        (watchedTrackAndTrace || null) !==
                          (container.track_and_trace || null),
                    },
                  ],
                };
              })}
            />

            <div style={{ marginTop: 16 }}>
              <BulkUpdateDiffPreview
                title={`Bookings (${updatePreview.bookings.length})`}
                emptyText="No bookings to update"
                entities={updatePreview.bookings.map((booking) => ({
                  key: booking.id,
                  title: formatBookingEffReferenceFromParts(booking.id),
                  fields: [
                    {
                      label: "Status",
                      oldValue: bookingStatusTag(booking.status),
                      newValue: bookingStatusTag(
                        bookingNewStatus || booking.status
                      ),
                      changed:
                        !!bookingNewStatus &&
                        bookingNewStatus !== (booking.status || null),
                    },
                  ],
                }))}
              />
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};
